/**
 * Backend `ErrorCodes` this app branches on, rather than merely displays.
 *
 * `localizeApiError` already turns any code into `ERRORS.<CODE>` without knowing it, so a code
 * only earns a constant here when the UI has to *do* something with it — reload because the form
 * locked underneath the venue, or mark the offending field instead of showing a toast.
 */
export const ApiErrorCodes = {
  /**
   * SmartPlayr closed the management app to this venue while a session was open. Every admin call
   * now answers this; the interceptor turns the first one into the closed screen.
   */
  ManagementAccessDisabled: 'MANAGEMENT_ACCESS_DISABLED',
  /** The schema was rejected: `fieldErrors { key: code }` says which field and why. */
  EventFormInvalid: 'EVENT_FORM_INVALID',
  /**
   * A change the lock forbids — the first registration landed while this tab was open.
   * Also carries `fieldErrors`, so the field that may no longer change is marked in place.
   */
  EventFormLocked: 'EVENT_FORM_LOCKED',
  EventNotFound: 'EVENT_NOT_FOUND',
  EventRegistrationNotFound: 'EVENT_REGISTRATION_NOT_FOUND',
  /** The EVENTS flag went off under an open tab: the surface no longer exists for this venue. */
  EventsNotEnabled: 'EVENTS_NOT_ENABLED',
  EventInvalidStatusTransition: 'EVENT_INVALID_STATUS_TRANSITION',
  EventTemplateNotFound: 'EVENT_TEMPLATE_NOT_FOUND',
  /**
   * The venue already has a connected account, so there is nothing to create. Not a failure to
   * report: it means the status this card was working from was behind, and the answer is to read
   * it again rather than to leave an owner pressing a button that can never succeed.
   */
  StripeAccountAlreadyExists: 'STRIPE_ACCOUNT_ALREADY_EXISTS',
  /**
   * No connected account exists yet, so there is no account session to embed. The venue has to
   * create one first — the embedded onboarding component cannot create the account it would
   * onboard.
   */
  StripeConnectNotConnected: 'STRIPE_CONNECT_NOT_CONNECTED',
  /** The session asked for a component this platform is not enabled for. A wiring bug, not a venue one. */
  StripeConnectUnsupportedComponent: 'STRIPE_CONNECT_UNSUPPORTED_COMPONENT',
} as const;

/** Error codes whose body carries per-field detail the form builder can mark inline. */
export const FIELD_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  ApiErrorCodes.EventFormInvalid,
  ApiErrorCodes.EventFormLocked,
]);
